import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { FaEnvelope, FaPhone, FaVideo } from "react-icons/fa";

const GENDER_OPTIONS = [
  "Female",
  "Male",
  "Woman",
  "Man",
  "Non-binary",
  "Prefer not to say",
];

const RACE_OPTIONS = [
  "Asian",
  "White",
  "Chinese American",
  "Hispanic or Latino",
  "Black or African American",
  "Native American",
  "Pacific Islander",
  "Middle Eastern",
  "South Asian",
  "Southeast Asian",
  "Multiracial",
  "Prefer not to say",
];

const LANGUAGE_OPTIONS = [
  "English",
  "Spanish",
  "Mandarin",
  "Cantonese",
  "French",
  "Portuguese",
  "Japanese",
  "Korean",
  "German",
  "Italian",
  "Arabic",
];

const POPULATION_OPTIONS = [
  "Adults (18-64)",
  "Adolescents (13-17)",
  "Children (5-12)",
  "Geriatric (65+)",
  "Couples",
  "LGBTQ+",
  "Veterans",
  "Pregnant/Postpartum",
  "Individuals with Disabilities",
  "Immigrants/Refugees",
  "First Responders",
  "College Students",
];

const SPECIALTY_OPTIONS = [
  "Anxiety",
  "Depression",
  "PTSD",
  "CBT",
  "Trauma",
  "Mindfulness",
  "Grief",
  "Relationship Issues",
  "ADHD",
  "Anger Management",
  "Life Transitions",
  "Chronic Illness",
  "Self-esteem",
  "Substance Abuse",
  "Eating disorders",
  "OCD",
  "Bipolar Disorder",
  "Sleep Issues",
  "Parenting",
  "Couples Therapy",
  "Family Conflict",
  "Stress Management",
  "Social Anxiety",
  "Autism Spectrum",
  "Phobias",
  "Body Image",
  "Postpartum",
  "Burnout",
  "Addiction",
  "Panic Disorders",
  "Work-Life Balance",
  "Attachment Issues",
  "Narcissistic Abuse",
  "Codependency",
  "Gender Identity",
  "Cultural Adjustment",
  "Elderly Care",
  "Anger Issues",
  "Emotional Regulation",
];

const MAX_TOP_SPECIALTIES = 3;
const SPECIALTY_TOP_META = "__TOP__";

const mergeOptions = (options, selected) => {
  const extras = (selected || []).filter(
    (item) => item && !options.includes(item),
  );
  return extras.length ? [...options, ...extras] : options;
};

const toList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (!value) return [];
  return String(value)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
};

/** Parse specialisation into selected + starred (top) lists. */
const parseSpecialties = (value) => {
  const parts = toList(value);
  const meta = parts.find((item) => item.startsWith(`${SPECIALTY_TOP_META}=`));
  const list = parts.filter(
    (item) => !item.startsWith(`${SPECIALTY_TOP_META}=`),
  );

  if (meta) {
    const count = Math.min(
      MAX_TOP_SPECIALTIES,
      Math.max(0, Number(meta.split("=")[1]) || 0),
    );
    return {
      list,
      starred: list.slice(0, count),
    };
  }

  // Legacy: first 3 were treated as top specialties
  return {
    list,
    starred: list.slice(0, Math.min(MAX_TOP_SPECIALTIES, list.length)),
  };
};

const serializeSpecialties = (list = [], starred = []) => {
  const selected = (list || []).filter(Boolean);
  const tops = (starred || [])
    .filter((item) => selected.includes(item))
    .slice(0, MAX_TOP_SPECIALTIES);
  const rest = selected.filter((item) => !tops.includes(item));
  const ordered = [...tops, ...rest];
  if (!ordered.length) return "";
  return `${SPECIALTY_TOP_META}=${tops.length},${ordered.join(",")}`;
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getPhoneDigits = (value) => {
  let digits = String(value || "").replace(/\D/g, "");
  // Strip leading US country code so masking stays (XXX) XXX-XXXX
  if (digits.length === 11 && digits.startsWith("1")) {
    digits = digits.slice(1);
  }
  return digits;
};

/** Format as (XXX) XXX-XXXX while the user types. */
const formatPhoneInput = (value) => {
  const digits = getPhoneDigits(value).slice(0, 10);
  const len = digits.length;
  if (!len) return "";
  if (len <= 3) return `(${digits}`;
  if (len <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const getPhoneValidationError = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";
  const digits = getPhoneDigits(trimmed);
  if (digits.length < 10) {
    return "Enter a 10-digit phone number.";
  }
  if (digits.length > 10) {
    return "Phone number cannot exceed 10 digits.";
  }
  return "";
};

const isValidPhone = (value) => !getPhoneValidationError(value);

const buildDrafts = (provider) => {
  const { list: specialties, starred: starredSpecialties } = parseSpecialties(
    provider?.specialisation,
  );
  return {
    personal: {
      firstname: provider?.firstname || "",
      lastname: provider?.lastname || "",
      displayName:
        provider?.displayName ||
        `${provider?.firstname || ""} ${provider?.lastname || ""}`.trim(),
      providerType: provider?.providerType || "Therapist",
      yearsOfExperience:
        provider?.yearsOfExperience !== undefined &&
        provider?.yearsOfExperience !== null
          ? provider.yearsOfExperience
          : "",
    },
    background: {
      genderIdentity: provider?.genderIdentity || provider?.gender || "",
      raceEthnicity: toList(provider?.raceEthnicity || provider?.race),
      languages: toList(provider?.languages),
      populationsServed: toList(provider?.populationsServed),
    },
    contact: {
      email: provider?.email || "",
      phone: formatPhoneInput(
        provider?.phone || provider?.phoneNumber || provider?.phoneno || "",
      ),
    },
    bio: {
      bio: provider?.bio || provider?.shortBio || "",
    },
    specialties: {
      list: specialties,
      starred: starredSpecialties,
    },
  };
};

const ReadonlySection = ({
  title,
  subtitle,
  onEdit,
  editLabel = "Edit",
  extraActions,
  children,
}) => (
  <div className="pp-section">
    <div className="pp-section-header">
      <div>
        <h3>{title}</h3>
        {subtitle && <p className="pp-section-header-subtitle">{subtitle}</p>}
      </div>
      <div className="pp-section-header-actions">
        {extraActions}
        {onEdit && (
          <button type="button" className="pp-section-edit" onClick={onEdit}>
            {editLabel}
          </button>
        )}
      </div>
    </div>
    <div className="pp-section-body">{children}</div>
  </div>
);

const InfoGrid = ({ items, columns = "auto" }) => (
  <div
    className={`pp-info-grid ${
      columns === 2
        ? "pp-info-grid-2"
        : columns === "personal"
          ? "pp-info-grid-personal"
          : ""
    }`.trim()}
    style={
      columns === "personal"
        ? { "--pp-personal-cols": items.length }
        : undefined
    }
  >
    {items.map((item) => (
      <div key={item.label} className="pp-info-item">
        <div className="pp-info-label">{item.label}</div>
        <div className="pp-info-value">{item.value || "—"}</div>
      </div>
    ))}
  </div>
);

const TagList = ({ tags, variant = "default", emptyLabel = "Not added yet" }) => {
  if (!tags?.length) return <span className="pp-muted">{emptyLabel}</span>;
  return (
    <div className="pp-tag-list">
      {tags.map((tag) => (
        <span key={tag} className={`pp-tag pp-tag-${variant}`}>
          {variant === "top" && <span className="pp-tag-star">★</span>}
          {tag}
        </span>
      ))}
    </div>
  );
};

const SectionFooter = ({
  onCancel,
  onSave,
  saving,
  summary,
  hideUnsaved = false,
}) => (
  <div className="pp-card-footer">
    <span className="pp-unsaved-label">
      {summary || (!hideUnsaved ? "Unsaved changes" : "")}
    </span>
    <div className="pp-card-footer-actions">
      <button
        type="button"
        className="pp-btn-cancel"
        onClick={onCancel}
        disabled={saving}
      >
        Cancel
      </button>
      <button
        type="button"
        className="pp-btn-save"
        onClick={onSave}
        disabled={saving}
      >
        {saving ? "Saving..." : "Save Changes"}
      </button>
    </div>
  </div>
);

/** Custom select that always opens the menu below the field. */
const DropdownSelect = ({
  id,
  value,
  options,
  onChange,
  placeholder = "Select",
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const selectedLabel =
    options.find((opt) => opt === value) || placeholder;

  return (
    <div
      className={`pp-dropdown ${open ? "is-open" : ""} ${className}`}
      ref={rootRef}
    >
      <button
        id={id}
        type="button"
        className="pp-form-input pp-form-select pp-dropdown-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span
          className={`pp-dropdown-value ${
            value ? "" : "pp-dropdown-placeholder"
          }`}
        >
          {selectedLabel}
        </span>
      </button>
      {open && (
        <ul className="pp-dropdown-menu" role="listbox">
          {options.map((opt) => {
            const isSelected = value === opt;
            return (
              <li
                key={opt}
                role="option"
                aria-selected={isSelected}
                className={`pp-dropdown-option ${
                  isSelected ? "is-selected" : ""
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange(opt);
                  setOpen(false);
                }}
              >
                <span className="pp-dropdown-option-label">{opt}</span>
                {isSelected && (
                  <svg
                    className="pp-dropdown-check"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M3.5 8.5L6.5 11.5L12.5 4.5"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const HollowStarIcon = () => (
  <svg
    className="pp-select-chip-star-icon"
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M12 3.5l2.6 5.3 5.9.9-4.3 4.2 1 5.8L12 16.9 6.8 19.7l1-5.8L3.5 9.7l5.9-.9L12 3.5z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
  </svg>
);

const FilledStarIcon = () => (
  <svg
    className="pp-select-chip-star-icon"
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M12 3.5l2.6 5.3 5.9.9-4.3 4.2 1 5.8L12 16.9 6.8 19.7l1-5.8L3.5 9.7l5.9-.9L12 3.5z" />
  </svg>
);

const ChipSelect = ({ options, selected, onToggle }) => (
  <div className="pp-chip-grid">
    {options.map((option) => {
      const isOn = selected.includes(option);
      return (
        <button
          key={option}
          type="button"
          className={`pp-select-chip ${isOn ? "selected" : ""}`}
          onClick={() => onToggle(option)}
        >
          {option}
        </button>
      );
    })}
  </div>
);

const SpecialtyChipSelect = ({
  options,
  selected,
  starred,
  starLimit = MAX_TOP_SPECIALTIES,
  onToggleSelect,
  onToggleStar,
}) => {
  const starredCount = starred.length;
  const atStarLimit = starredCount >= starLimit;
  const selectedCount = selected.length;

  return (
    <div className="pp-specialty-editor">
      <div className="pp-specialty-toolbar">
        <p className="pp-specialty-hint">
          Select all that apply. Star up to{" "}
          <strong>{starLimit} top specialties</strong> to highlight on your
          profile.
        </p>
        <span className="pp-specialty-star-count">
          {starredCount}/{starLimit} starred
        </span>
      </div>

      <div className="pp-chip-grid">
        {options.map((option) => {
          const isOn = selected.includes(option);
          const isStarred = starred.includes(option);
          return (
            <button
              key={option}
              type="button"
              className={`pp-select-chip ${isOn ? "selected" : ""} ${
                isStarred ? "starred" : ""
              }`}
              onClick={() => onToggleSelect(option)}
            >
              {isOn && (
                <span
                  className={`pp-select-chip-star ${
                    isStarred ? "filled" : "hollow"
                  } ${
                    !isStarred && atStarLimit
                      ? "pp-select-chip-star-blocked"
                      : ""
                  }`}
                  role="button"
                  tabIndex={0}
                  aria-label={
                    isStarred
                      ? `Unstar ${option}`
                      : atStarLimit
                        ? `Star limit reached (${starLimit}). Unstar one first.`
                        : `Star ${option} as top specialty`
                  }
                  title={
                    !isStarred && atStarLimit
                      ? `Only ${starLimit} top specialties allowed. Unstar one first.`
                      : undefined
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleStar(option);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      onToggleStar(option);
                    }
                  }}
                >
                  {isStarred ? <FilledStarIcon /> : <HollowStarIcon />}
                </span>
              )}
              <span className="pp-select-chip-label">{option}</span>
            </button>
          );
        })}
      </div>

      <p className="pp-specialty-footer-meta" aria-live="polite">
        {selectedCount} selected · {starredCount} top{" "}
        {starredCount === 1 ? "specialty" : "specialties"}
      </p>
    </div>
  );
};

const ProviderProfileTab = ({
  provider,
  isTherapist,
  isOwnProfile = false,
  getProviderId,
  onProviderUpdated,
}) => {
  const [drafts, setDrafts] = useState(() => buildDrafts(provider));
  const [editingSection, setEditingSection] = useState(null);
  const [savingSection, setSavingSection] = useState(null);
  const [showRemoveVideoConfirm, setShowRemoveVideoConfirm] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const videoInputRef = useRef(null);

  useEffect(() => {
    if (!editingSection) {
      setDrafts(buildDrafts(provider));
    }
  }, [provider, editingSection]);

  const startEdit = (section) => {
    setDrafts(buildDrafts(provider));
    setEmailError("");
    setPhoneError("");
    setEditingSection(section);
  };

  const cancelEdit = () => {
    setDrafts(buildDrafts(provider));
    setEmailError("");
    setPhoneError("");
    setEditingSection(null);
  };

  const updateDraft = (section, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const toggleListValue = (section, field, option) => {
    setDrafts((prev) => {
      const current = prev[section][field] || [];
      const exists = current.includes(option);
      return {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: exists
            ? current.filter((v) => v !== option)
            : [...current, option],
        },
      };
    });
  };

  const toggleSpecialtySelect = (option) => {
    setDrafts((prev) => {
      const list = prev.specialties.list || [];
      const starred = prev.specialties.starred || [];
      const exists = list.includes(option);
      if (exists) {
        return {
          ...prev,
          specialties: {
            list: list.filter((v) => v !== option),
            starred: starred.filter((v) => v !== option),
          },
        };
      }
      return {
        ...prev,
        specialties: {
          list: [...list, option],
          starred,
        },
      };
    });
  };

  const toggleSpecialtyStar = (option) => {
    setDrafts((prev) => {
      const list = prev.specialties.list || [];
      const starred = prev.specialties.starred || [];
      if (!list.includes(option)) return prev;

      if (starred.includes(option)) {
        return {
          ...prev,
          specialties: {
            list,
            starred: starred.filter((v) => v !== option),
          },
        };
      }

      if (starred.length >= MAX_TOP_SPECIALTIES) {
        toast.info(
          `Only ${MAX_TOP_SPECIALTIES} top specialties allowed. Unstar one first.`,
        );
        return prev;
      }

      return {
        ...prev,
        specialties: {
          list,
          starred: [...starred, option],
        },
      };
    });
  };

  const saveSection = async (section, payload) => {
    try {
      setSavingSection(section);
      const request = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/specialist/editprovider/${getProviderId()}`,
        {
          email: provider.email,
          firstname: provider.firstname,
          lastname: provider.lastname,
          providerType: provider.providerType,
          yearsOfExperience: provider.yearsOfExperience,
          specialisation: provider.specialisation,
          gender: provider.genderIdentity || provider.gender,
          genderIdentity: provider.genderIdentity || provider.gender,
          displayName: provider.displayName,
          raceEthnicity: provider.raceEthnicity || provider.race,
          languages: provider.languages,
          populationsServed: provider.populationsServed,
          bio: provider.bio || provider.shortBio,
          shortBio: provider.bio || provider.shortBio,
          phoneno: provider.phoneno || provider.phone || provider.phoneNumber,
          phone: provider.phone || provider.phoneNumber || provider.phoneno,
          // Only send credentials/insurance when they are valid arrays —
          // never re-post corrupted "[object Object]" strings.
          ...(Array.isArray(provider.credentials)
            ? { credentials: provider.credentials }
            : {}),
          ...(Array.isArray(provider.insurance)
            ? { insurance: provider.insurance }
            : {}),
          fees: provider.fees,
          time_zone: provider.time_zone,
          ...payload,
        },
      );

      if (request.data.success) {
        const next = {
          ...provider,
          ...payload,
          ...(request.data.data || {}),
        };
        onProviderUpdated(next);
        setEditingSection(null);
        toast.success("Changes saved successfully.");
      } else {
        toast.error(request.data.message || "Failed to save changes.");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Network error. Please try again.",
      );
    } finally {
      setSavingSection(null);
    }
  };

  const handlePersonalSave = () => {
    const info = drafts.personal;
    if (!info.firstname?.trim() || !info.lastname?.trim()) {
      toast.error("First Name and Last Name are required.");
      return;
    }
    if (!info.displayName?.trim()) {
      toast.error("Display Name is required.");
      return;
    }
    const isTherapistType = isOwnProfile
      ? !provider.providerType || provider.providerType === "Therapist"
      : !info.providerType || info.providerType === "Therapist";
    if (
      isTherapistType &&
      (info.yearsOfExperience === "" ||
        info.yearsOfExperience === null ||
        Number(info.yearsOfExperience) < 0)
    ) {
      toast.error("Years of Experience is required.");
      return;
    }

    saveSection("personal", {
      firstname: info.firstname.trim(),
      lastname: info.lastname.trim(),
      displayName: info.displayName.trim(),
      ...(isOwnProfile
        ? {}
        : { providerType: info.providerType || "Therapist" }),
      yearsOfExperience: isTherapistType
        ? Number(info.yearsOfExperience)
        : null,
    });
  };

  const handleBackgroundSave = () => {
    const bg = drafts.background;
    saveSection("background", {
      gender: bg.genderIdentity || null,
      genderIdentity: bg.genderIdentity || null,
      race: bg.raceEthnicity.length ? bg.raceEthnicity.join(", ") : null,
      raceEthnicity: bg.raceEthnicity.length
        ? bg.raceEthnicity.join(", ")
        : null,
      languages: bg.languages.length ? bg.languages.join(", ") : null,
      populationsServed: bg.populationsServed.length
        ? bg.populationsServed.join(", ")
        : null,
    });
  };

  const handleContactSave = () => {
    const contact = drafts.contact;
    const email = contact.email?.trim() || "";
    const phone = (contact.phone || "").trim();
    let hasError = false;

    if (!email) {
      setEmailError("Email is required.");
      hasError = true;
    } else if (!EMAIL_REGEX.test(email)) {
      setEmailError("Enter a valid email address.");
      hasError = true;
    } else {
      setEmailError("");
    }

    if (!isValidPhone(phone)) {
      setPhoneError(
        getPhoneValidationError(phone) ||
          "Enter a valid 10-digit phone number.",
      );
      hasError = true;
    } else {
      setPhoneError("");
    }

    if (hasError) {
      toast.error("Please fix the contact fields and try again.");
      return;
    }

    saveSection("contact", {
      email,
      phone,
      phoneNumber: phone,
      phoneno: phone,
    });
  };

  const handleBioSave = () => {
    saveSection("bio", {
      bio: drafts.bio.bio || "",
      shortBio: drafts.bio.bio || "",
    });
  };

  const handleSpecialtiesSave = () => {
    const list = drafts.specialties.list || [];
    const starred = drafts.specialties.starred || [];
    saveSection("specialties", {
      specialisation: list.length ? serializeSpecialties(list, starred) : "",
    });
  };

  const handleVideoReplace = async (file) => {
    if (!file) return;
    try {
      setSavingSection("video");
      const formData = new FormData();
      formData.append("videoIntro", file);
      Object.keys(provider).forEach((key) => {
        if (
          key !== "videoIntro" &&
          key !== "videoIntroUrl" &&
          provider[key] !== null &&
          provider[key] !== undefined
        ) {
          formData.append(key, provider[key]);
        }
      });

      const request = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/specialist/editprovider/${getProviderId()}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      if (request.data.success) {
        onProviderUpdated({
          ...provider,
          videoIntroUrl:
            request.data.data?.videoIntroUrl || provider.videoIntroUrl,
        });
        toast.success("Intro video updated.");
      } else {
        toast.error(request.data.message || "Failed to update video.");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to upload video.");
    } finally {
      setSavingSection(null);
      if (videoInputRef.current) videoInputRef.current.value = "";
    }
  };

  const handleVideoRemove = async () => {
    try {
      setSavingSection("video");
      const request = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/specialist/editprovider/${getProviderId()}`,
        {
          ...provider,
          videoIntroUrl: "",
          videoIntro: null,
        },
      );
      if (request.data.success) {
        onProviderUpdated({
          ...provider,
          videoIntroUrl: "",
        });
        setShowRemoveVideoConfirm(false);
        toast.success("Intro video removed.");
      } else {
        toast.error(request.data.message || "Failed to remove video.");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to remove video.");
    } finally {
      setSavingSection(null);
    }
  };

  const personal = drafts.personal;
  const background = drafts.background;
  const contact = drafts.contact;

  const displayName =
    provider.displayName ||
    `${provider.firstname || ""} ${provider.lastname || ""}`.trim() ||
    "—";
  const providerType = provider.providerType || "Therapist";
  const genderIdentity = provider.genderIdentity || provider.gender || "";
  const raceEthnicity = toList(provider.raceEthnicity || provider.race);
  const languages = toList(provider.languages);
  const populations = toList(provider.populationsServed);
  const bio = provider.bio || provider.shortBio || "";
  const { list: specialtyList, starred: topSpecialties } = parseSpecialties(
    provider.specialisation,
  );
  const otherSpecialties = specialtyList.filter(
    (item) => !topSpecialties.includes(item),
  );
  const phone = formatPhoneInput(
    provider.phone || provider.phoneNumber || provider.phoneno || "",
  );
  const yearsOfExperienceDisplay =
    provider.yearsOfExperience !== undefined &&
    provider.yearsOfExperience !== null &&
    provider.yearsOfExperience !== ""
      ? String(provider.yearsOfExperience)
      : "-";
  const personalInfoViewItems = [
    { label: "First Name", value: provider.firstname || "-" },
    { label: "Last Name", value: provider.lastname || "-" },
    { label: "Display Name", value: displayName === "—" ? "-" : displayName },
    ...(isTherapist
      ? [{ label: "Years of Experience", value: yearsOfExperienceDisplay }]
      : []),
    ...(!isOwnProfile ? [{ label: "Role", value: providerType }] : []),
  ];

  return (
    <>
      {/* Personal Info */}
      {editingSection === "personal" ? (
        <div className="pp-card">
          <div className="pp-card-header">
            <div>
              <h3 className="pp-card-title">Personal Info</h3>
              <p className="pp-card-subtitle">
                {isOwnProfile
                  ? "Basic identity details."
                  : "Basic identity and professional role details."}
              </p>
            </div>
          </div>
          <div className="pp-card-body">
            <div className="pp-form-grid pp-form-grid-2 pp-form-grid-personal">
              <div className="pp-form-field">
                <label className="pp-info-label">First Name</label>
                <input
                  className="pp-form-input"
                  value={personal.firstname}
                  onChange={(e) =>
                    updateDraft("personal", "firstname", e.target.value)
                  }
                />
              </div>
              <div className="pp-form-field">
                <label className="pp-info-label">Last Name</label>
                <input
                  className="pp-form-input"
                  value={personal.lastname}
                  onChange={(e) =>
                    updateDraft("personal", "lastname", e.target.value)
                  }
                />
              </div>
              <div className="pp-form-field">
                <label className="pp-info-label">Display Name</label>
                <input
                  className="pp-form-input"
                  value={personal.displayName}
                  onChange={(e) =>
                    updateDraft("personal", "displayName", e.target.value)
                  }
                />
              </div>
              {(isOwnProfile
                ? isTherapist
                : (personal.providerType || "Therapist") === "Therapist") && (
                <div className="pp-form-field">
                  <label className="pp-info-label">Years of Experience</label>
                  <input
                    type="number"
                    min="0"
                    max="60"
                    className="pp-form-input"
                    value={personal.yearsOfExperience}
                    onChange={(e) =>
                      updateDraft(
                        "personal",
                        "yearsOfExperience",
                        e.target.value,
                      )
                    }
                  />
                </div>
              )}
              {!isOwnProfile &&
                (personal.providerType || "Therapist") !== "Therapist" && (
                  <div className="pp-form-field">
                    <label className="pp-info-label">Role</label>
                    <DropdownSelect
                      value={personal.providerType || "Therapist"}
                      options={[
                        "Therapist",
                        "Care Manager",
                        "Psychiatric Consultant",
                      ]}
                      placeholder="Select"
                      onChange={(value) =>
                        updateDraft(
                          "personal",
                          "providerType",
                          value || "Therapist",
                        )
                      }
                    />
                  </div>
                )}
            </div>
            {!isOwnProfile &&
              (personal.providerType || "Therapist") === "Therapist" && (
                <div className="pp-form-grid pp-form-grid-2 pp-form-grid-personal pp-form-grid-personal-follow">
                  <div className="pp-form-field">
                    <label className="pp-info-label">Role</label>
                    <DropdownSelect
                      value={personal.providerType || "Therapist"}
                      options={[
                        "Therapist",
                        "Care Manager",
                        "Psychiatric Consultant",
                      ]}
                      placeholder="Select"
                      onChange={(value) =>
                        updateDraft(
                          "personal",
                          "providerType",
                          value || "Therapist",
                        )
                      }
                    />
                  </div>
                </div>
              )}
            <SectionFooter
              saving={savingSection === "personal"}
              onCancel={cancelEdit}
              onSave={handlePersonalSave}
            />
          </div>
        </div>
      ) : (
        <ReadonlySection
          title="Personal Info"
          onEdit={() => startEdit("personal")}
        >
          <InfoGrid columns="personal" items={personalInfoViewItems} />
        </ReadonlySection>
      )}

      {/* Background & Identity */}
      {isTherapist &&
        (editingSection === "background" ? (
          <div className="pp-card">
            <div className="pp-card-header">
              <div>
                <h3 className="pp-card-title">Background & Identity</h3>
                <p className="pp-card-subtitle">
                  Demographics, spoken languages, and populations served.
                </p>
              </div>
            </div>
            <div className="pp-card-body">
              <div className="pp-form-grid pp-form-grid-2 pp-form-grid-full">
                <div className="pp-form-field">
                  <label className="pp-info-label">Gender Identity</label>
                  <DropdownSelect
                    value={background.genderIdentity}
                    options={mergeOptions(GENDER_OPTIONS, [
                      background.genderIdentity,
                    ])}
                    placeholder="Select"
                    onChange={(value) =>
                      updateDraft("background", "genderIdentity", value)
                    }
                  />
                </div>
                <div className="pp-form-field">
                  <label className="pp-info-label">Race & Ethnicity</label>
                  <DropdownSelect
                    value={background.raceEthnicity[0] || ""}
                    options={mergeOptions(
                      RACE_OPTIONS,
                      background.raceEthnicity,
                    )}
                    placeholder="Select"
                    onChange={(value) =>
                      updateDraft(
                        "background",
                        "raceEthnicity",
                        value ? [value] : [],
                      )
                    }
                  />
                </div>
              </div>

              <div className="pp-chip-section">
                <div className="pp-info-label">Languages Spoken</div>
                <ChipSelect
                  options={mergeOptions(LANGUAGE_OPTIONS, background.languages)}
                  selected={background.languages}
                  onToggle={(opt) =>
                    toggleListValue("background", "languages", opt)
                  }
                />
              </div>

              <div className="pp-chip-section">
                <div className="pp-info-label">Populations Served</div>
                <ChipSelect
                  options={mergeOptions(
                    POPULATION_OPTIONS,
                    background.populationsServed,
                  )}
                  selected={background.populationsServed}
                  onToggle={(opt) =>
                    toggleListValue("background", "populationsServed", opt)
                  }
                />
              </div>

              <SectionFooter
                saving={savingSection === "background"}
                onCancel={cancelEdit}
                onSave={handleBackgroundSave}
              />
            </div>
          </div>
        ) : (
          <ReadonlySection
            title="Background & Identity"
            subtitle="Visible to patients when browsing providers."
            onEdit={() => startEdit("background")}
          >
            <div className="pp-background-block">
              <div className="pp-background-row">
                <span className="pp-info-label">Gender Identity</span>
                <TagList
                  tags={genderIdentity ? [genderIdentity] : []}
                  emptyLabel="Not added yet"
                />
              </div>
              <div className="pp-background-row">
                <span className="pp-info-label">Race & Ethnicity</span>
                <TagList
                  tags={raceEthnicity}
                  emptyLabel="Not added yet"
                />
              </div>
              <div className="pp-background-row">
                <span className="pp-info-label">Languages Spoken</span>
                <TagList tags={languages} emptyLabel="Not added yet" />
              </div>
              <div className="pp-background-row">
                <span className="pp-info-label">Populations Served</span>
                <TagList tags={populations} emptyLabel="Not added yet" />
              </div>
            </div>
          </ReadonlySection>
        ))}

      {/* Contact Info */}
      {editingSection === "contact" ? (
        <div className="pp-card">
          <div className="pp-card-header">
            <div>
              <h3 className="pp-card-title">Contact Info</h3>
              <p className="pp-card-subtitle">
                Required administrative contact and verification identifiers.
              </p>
            </div>
          </div>
          <div className="pp-card-body">
            <div className="pp-form-grid pp-form-grid-2 pp-form-grid-full">
              <div className="pp-form-field">
                <label className="pp-info-label">Email</label>
                <input
                  type="email"
                  className={`pp-form-input ${emailError ? "pp-form-input-error" : ""}`}
                  value={contact.email}
                  onChange={(e) => {
                    setEmailError("");
                    updateDraft("contact", "email", e.target.value);
                  }}
                  placeholder="provider@email.com"
                />
                {emailError && (
                  <span className="pp-field-error">{emailError}</span>
                )}
              </div>
              <div className="pp-form-field">
                <label className="pp-info-label">Phone</label>
                <input
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  className={`pp-form-input ${phoneError ? "pp-form-input-error" : ""}`}
                  value={contact.phone}
                  onChange={(e) => {
                    const next = formatPhoneInput(e.target.value);
                    updateDraft("contact", "phone", next);
                    setPhoneError(getPhoneValidationError(next));
                  }}
                  onBlur={() => {
                    const next = formatPhoneInput(contact.phone);
                    updateDraft("contact", "phone", next);
                    setPhoneError(getPhoneValidationError(next));
                  }}
                  placeholder="(555) 555-5555"
                  maxLength={14}
                  aria-invalid={Boolean(phoneError)}
                />
                {phoneError && (
                  <span className="pp-field-error">{phoneError}</span>
                )}
              </div>
            </div>
            <SectionFooter
              saving={savingSection === "contact"}
              onCancel={cancelEdit}
              onSave={handleContactSave}
            />
          </div>
        </div>
      ) : (
        <ReadonlySection
          title="Contact Info"
          subtitle="Not visible to patients."
          onEdit={() => startEdit("contact")}
        >
          <div className="pp-contact-list">
            <div className="pp-contact-row">
              <FaEnvelope className="pp-contact-icon" />
              <div>
                <div className="pp-info-label">Email</div>
                <div className="pp-info-value">
                  {provider.email || "Not added yet"}
                </div>
              </div>
            </div>
            <div className="pp-contact-row">
              <FaPhone className="pp-contact-icon" />
              <div>
                <div className="pp-info-label">Phone</div>
                <div className={`pp-info-value ${!phone ? "pp-muted" : ""}`}>
                  {phone || "Not added yet"}
                </div>
              </div>
            </div>
          </div>
        </ReadonlySection>
      )}

      {/* Short Bio */}
      {isTherapist &&
        (editingSection === "bio" ? (
          <div className="pp-card">
            <div className="pp-card-header">
              <div>
                <h3 className="pp-card-title">Short Bio</h3>
                <p className="pp-card-subtitle">
                  A brief professional statement for the patient directory.
                </p>
              </div>
            </div>
            <div className="pp-card-body">
              <textarea
                className="pp-form-input pp-form-textarea"
                rows={6}
                value={drafts.bio.bio}
                onChange={(e) => updateDraft("bio", "bio", e.target.value)}
                placeholder="Write a short bio patients will see..."
              />
              <SectionFooter
                saving={savingSection === "bio"}
                onCancel={cancelEdit}
                onSave={handleBioSave}
              />
            </div>
          </div>
        ) : (
          <ReadonlySection
            title="Short Bio"
            subtitle="Visible to patients when browsing providers."
            onEdit={() => startEdit("bio")}
          >
            {bio ? (
              <p className="pp-bio-text">{bio}</p>
            ) : (
              <p className="pp-muted">No bio added yet.</p>
            )}
          </ReadonlySection>
        ))}

      {/* Specialties */}
      {isTherapist &&
        (editingSection === "specialties" ? (
          <div className="pp-card">
            <div className="pp-card-header">
              <div>
                <h3 className="pp-card-title">Specialties</h3>
                <p className="pp-card-subtitle">
                  Visible to patients when browsing providers.
                </p>
              </div>
            </div>
            <div className="pp-card-body">
              <SpecialtyChipSelect
                options={mergeOptions(
                  SPECIALTY_OPTIONS,
                  drafts.specialties.list,
                )}
                selected={drafts.specialties.list || []}
                starred={drafts.specialties.starred || []}
                starLimit={MAX_TOP_SPECIALTIES}
                onToggleSelect={toggleSpecialtySelect}
                onToggleStar={toggleSpecialtyStar}
              />
              <SectionFooter
                saving={savingSection === "specialties"}
                onCancel={cancelEdit}
                onSave={handleSpecialtiesSave}
              />
            </div>
          </div>
        ) : (
          <ReadonlySection
            title="Specialties"
            subtitle="Visible to patients when browsing providers."
            onEdit={() => startEdit("specialties")}
          >
            {!specialtyList.length ? (
              <p className="pp-muted">No specialties selected.</p>
            ) : (
              <div className="pp-specialty-groups">
                <div>
                  <div className="pp-info-label" style={{ marginBottom: 8 }}>
                    Top Specialties
                  </div>
                  <TagList
                    tags={topSpecialties}
                    variant="top"
                    emptyLabel="No top specialties selected."
                  />
                </div>
                {otherSpecialties.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <div className="pp-info-label" style={{ marginBottom: 8 }}>
                      Also Specializes in
                    </div>
                    <TagList tags={otherSpecialties} />
                  </div>
                )}
              </div>
            )}
          </ReadonlySection>
        ))}

      {/* Intro Video */}
      {isTherapist && (
        <div className="pp-section pp-card-video">
          <div className="pp-section-header pp-video-header">
            <div>
              <h3 className="pp-video-title">Intro Video</h3>
              <p className="pp-section-header-subtitle pp-video-subtitle">
                Visible to patients when browsing providers.
              </p>
            </div>
            <div className="pp-video-actions">
              {provider.videoIntroUrl ? (
                <>
                  <button
                    type="button"
                    className="pp-video-remove-btn"
                    onClick={() => setShowRemoveVideoConfirm(true)}
                    disabled={savingSection === "video"}
                  >
                    Remove
                  </button>
                  <button
                    type="button"
                    className="pp-video-replace-btn"
                    onClick={() => videoInputRef.current?.click()}
                    disabled={savingSection === "video"}
                  >
                    Replace
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="pp-video-replace-btn"
                  onClick={() => videoInputRef.current?.click()}
                  disabled={savingSection === "video"}
                >
                  Add Video
                </button>
              )}
            </div>
          </div>
          <div className="pp-section-body">
            <input
              ref={videoInputRef}
              type="file"
              accept="video/*"
              hidden
              onChange={(e) => handleVideoReplace(e.target.files?.[0])}
            />
            {provider.videoIntroUrl ? (
              <div className="pp-video-box">
                <video
                  controls
                  className="pp-video-player"
                  src={provider.videoIntroUrl}
                />
              </div>
            ) : (
              <div className="pp-video-box">
                <div className="pp-video-placeholder">
                  <FaVideo size={28} />
                  <span>No intro video added yet.</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showRemoveVideoConfirm && (
        <div className="modal-overlay pp-confirm-overlay">
          <div
            className="modal-container pp-confirm-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pp-remove-video-title"
          >
            <p className="modal-text" id="pp-remove-video-title">
              Are you sure you want to remove this intro video?
            </p>
            <div className="pp-confirm-actions">
              <button
                type="button"
                className="confirm-button pp-confirm-btn-remove"
                onClick={handleVideoRemove}
                disabled={savingSection === "video"}
              >
                {savingSection === "video" ? "Removing..." : "Yes, remove"}
              </button>
              <button
                type="button"
                className="cancel-button pp-confirm-btn-cancel"
                onClick={() => setShowRemoveVideoConfirm(false)}
                disabled={savingSection === "video"}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProviderProfileTab;
